import { Disclosure } from '@headlessui/react'
import { EditPencil, Plus, WarningCircle } from 'iconoir-react'
import { compact, startCase } from 'lodash'
import moment from 'moment'
import { ReactElement, useEffect, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import ContactSearchModalLink from '../ContactSearchModalLink'
import MatchPatientModalLink from '../MatchPatientModalLink'
import { PatientStatusBadge } from '../PatientStatusBadge'
import { TypedReadOnlyV2 } from '../TypedFormFieldV2'
import { usePreferredProviders } from '../usePreferredProviders'
import { useProviderNames } from '../useProviderNames'
import { ChevronToggle } from 'app/components/ChevronToggle'
import Spinner from 'app/components/Spinner'
import { TagChip } from 'app/components/tags/TagChip'
import { getAge } from 'app/date.utils'
import { formatPhoneNumber } from 'app/formatPhoneNumber'
import type { ContactJson } from 'app/types/entities'
import type { LocationJson } from 'app/types/entities'
import type { PatientJson } from 'app/types/entities'
import { isLocationIntegrated } from 'app/utils'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'
import type { PreferredProvider } from 'lib/preferredProviders'
import removeNull from 'lib/removeNull'
import { gqlFetchList } from 'app/lib/graphqlClient'

export function buildPatientSearchQuery(patientIds: string[]): string {
  if (patientIds.length === 0) return ''
  // Use id.keyword (exact match) rather than the analyzed id field. Patient IDs
  // like `K-3964d5-32654` contain hyphens, which the analyzed field tokenizes,
  // causing the query to miss the record (or over-match unrelated ones).
  return `id.keyword:(${patientIds.join(' OR ')})`
}

export const disclosurePanelClassName = `flex flex-col gap-x-2 gap-y-4 pt-3 pb-2 px-2`

// A read-only "Preferred Provider / Hygienist" row matching the label/value
// layout of TypedReadOnlyV2, but whose value is the async-resolved provider
// name (via LazyKollaProvider) plus a muted "last visit" hint.
// Always rendered (even when there is no derived preferred) so the "Preferred
// Dentist" / "Preferred Hygienist" fields are consistently present, showing an
// em dash when empty like the other read-only fields.
function PreferredProviderRow({
  label,
  preferred,
  name,
}: {
  label: string
  preferred?: PreferredProvider
  name?: string
}): ReactElement {
  return (
    <div className="flex h-8 items-center gap-x-1">
      <div className="w-[131px] shrink-0 truncate text-body-xs text-subtle">{label}</div>
      <div className="flex min-w-0 flex-1 items-center gap-x-1 pl-2">
        {preferred ? (
          <>
            {name && <span className="truncate text-label-xs font-semibold text-strong">{name}</span>}
            <span className="whitespace-nowrap text-label-xs font-normal text-minimal">
              {name ? `· ` : ``}last visit {moment(preferred.lastSeenDate).format(`MMM YYYY`)}
            </span>
          </>
        ) : (
          <span className="text-label-xs font-semibold text-strong">—</span>
        )}
      </div>
    </div>
  )
}

// One linked-patient disclosure. Extracted so it can call usePreferredProviders
// per patient (hook rules forbid calling it inside a .map()).
function LinkedPatientRow({
  item,
  contact,
  providerNames,
  isLast,
}: {
  item: PatientJson
  contact: ContactJson
  providerNames: Map<string, string>
  isLast: boolean
}): ReactElement {
  const { preferredProvider, preferredHygienist } = usePreferredProviders(item.id)

  return (
    <Disclosure as="div" className={twMerge(`rounded-md border border-subtle bg-surface-subtle`, !isLast && `mb-1`)}>
      {({ open }) => (
        <>
          <Disclosure.Button className="flex w-full items-center justify-between gap-4 py-2 pl-4 pr-2 align-middle">
            <div className="flex items-center gap-2 overflow-hidden">
              <div className="flex overflow-hidden whitespace-nowrap text-ui-xs text-strong">
                <span className="trucate overflow-hidden">
                  {startCase(compact([item.firstName, item.lastName]).join(` `))}
                </span>
                <span className="shrink-0">{item.birthDate && `, ${getAge(item.birthDate)}`}</span>
              </div>
              <PatientStatusBadge status={item.status} />
            </div>
            <ChevronToggle open={open} className="pr-0" />
          </Disclosure.Button>
          <Disclosure.Panel as="div" className="px-4 pb-4 pt-0">
            <TypedReadOnlyV2<PatientJson>
              field="id"
              label="Patient ID"
              value={item.id}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <PreferredProviderRow
              label="Preferred Dentist"
              preferred={preferredProvider}
              name={preferredProvider ? providerNames.get(preferredProvider.providerId) : undefined}
            />
            <PreferredProviderRow
              label="Preferred Hygienist"
              preferred={preferredHygienist}
              name={preferredHygienist ? providerNames.get(preferredHygienist.providerId) : undefined}
            />
            <TypedReadOnlyV2<PatientJson>
              field="insuranceProvider"
              label="Insurance Provider"
              value={item.insuranceProvider}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2<PatientJson>
              field="subscriberId"
              label="Subscriber ID"
              value={item.subscriberId}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="patientStatus"
              label="Patient Status"
              value={item.status}
              className="!text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="preferredName"
              label="Preferred Name"
              value={item.preferredName}
              className="capitalized !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="cellPhone"
              label="Cell Phone"
              value={formatPhoneNumber(item.cellPhone)}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="email"
              label="Email"
              value={item.email}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="birthDate"
              label="Birth Date"
              value={item.birthDate}
              isDate
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2<PatientJson>
              field="addressLine1"
              label="Address Line 1"
              value={item.addressLine1}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2<PatientJson>
              field="addressLine2"
              label="Address Line 2"
              value={item.addressLine2}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2<PatientJson>
              field="city"
              label="City"
              value={item.city}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2<PatientJson>
              field="state"
              label="State"
              value={item.state}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2<PatientJson>
              field="zip"
              label="ZIP"
              value={item.zip}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="guarantorId"
              label="Guarantor ID"
              value={item.guarantorId}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="guarantorName"
              label="Guarantor Name"
              value={startCase(compact([item.guarantorFirstName, item.guarantorLastName]).join(` `))}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="subscriberId"
              label="Subscriber ID"
              value={item.subscriberId}
              className="phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="patientReferral"
              label="Patient Referral"
              value={item.patientReferral}
              className="capitalized phi !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="otherReferral"
              label="Other Referral"
              value={item.referralType}
              className="capitalized !text-label-xs !font-semibold !text-strong"
            />
            <TypedReadOnlyV2
              field="linkType"
              label="Link Type"
              value={contact?.matchTypes?.join(`, `)}
              className="capitalized !text-label-xs !font-semibold !text-strong"
            />
            <MatchPatientModalLink contact={contact} patient={item}>
              <div role="button" className="btn btn-sm btn-tertiary mt-2 flex w-full gap-x-2 bg-surface/10">
                <EditPencil width={16} height={16} /> Edit Patient Link
              </div>
            </MatchPatientModalLink>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  )
}

type Props = {
  contact: ContactJson
  location: LocationJson
}

export default function PmsIntegration({ contact, location }: Props): ReactElement {
  const isIntegrated = isLocationIntegrated(location)
  // Only resolve provider names when there's a linked patient to show them for —
  // otherwise the preferred rows never render and the fetch is wasted.
  const { names: providerNames } = useProviderNames(contact.patientIds?.length ? location.kollaConsumerId : undefined)
  const [patients, setPatients] = useState<PatientJson[]>()
  const [loadingPatient, setLoadingPatient] = useState<boolean>(true)
  useEffect(() => {
    if (contact?.patientIds?.length) {
      bg(async () => {
        // No field list: these patients are handed on to the Manage Patient Link modal, which
        // renders fields this panel does not. A hand-picked selection silently starves those
        // consumers (a missing locationId left the modal's location spinning forever), so take
        // the full model per AGENTS.md.
        const result = await gqlFetchList<PatientJson>('patients', {
          search: buildPatientSearchQuery(contact.patientIds!),
        })
        if (result.total > 0) {
          debug(`patient`, `patient linked found data:`, result.hits)
          setPatients(compact(result.hits.map((p) => removeNull<PatientJson>(p as PatientJson))))
        }
        setLoadingPatient(false)
      })
    } else setLoadingPatient(false)
  }, [contact])

  const hasPatients = patients?.length != null && patients?.length > 0

  return (
    <div className="flex w-full flex-col rounded-md bg-surface p-4">
      {loadingPatient ? (
        <div className="items-cente flex justify-center align-middle">
          <Spinner size={4} />
        </div>
      ) : isIntegrated ? (
        <Disclosure defaultOpen>
          {({ open }) => (
            <>
              <Disclosure.Button
                className={twMerge(
                  `flex w-full items-center justify-between`,
                  hasPatients && open && `mb-4`,
                  hasPatients ? `` : `cursor-default`,
                )}
              >
                <div className="flex w-full items-center gap-x-2">
                  <div className="text-heading-xs font-semibold text-strong">Linked Patient Data</div>
                  {hasPatients ? (
                    <>
                      <div className="flex h-5 items-center justify-center rounded border-[0.5px] border-default bg-primary-600 px-1.5">
                        <div className="text-label-xs text-on-color">{patients.length}</div>
                      </div>
                      <ChevronToggle open={open} />
                    </>
                  ) : (
                    <TagChip name="No Patient Linked" colorKey="bg-tags-orange" />
                  )}
                </div>
                <ContactSearchModalLink contact={contact} requireLinkedPatient>
                  <div
                    role="button"
                    className="size-6 flex items-center justify-center rounded-md border border-default"
                  >
                    <Plus width={16} height={16} className="text-default" />
                  </div>
                </ContactSearchModalLink>
              </Disclosure.Button>
              <Disclosure.Panel>
                {hasPatients ? (
                  patients
                    .slice()
                    .sort((a, b) => {
                      if (!(a.status && b.status)) return 0
                      const statusOrder = {
                        act: 1, //active
                        pat: 1, //patient
                        ina: 2, //inactive
                        non: 2, //nonpatient
                        del: 3, //deleted
                        arc: 3, //archived
                      }
                      return (
                        (statusOrder[a.status?.toLowerCase().substring(0, 3)] || 3) -
                        (statusOrder[b.status?.toLowerCase().substring(0, 3)] || 3)
                      )
                    })
                    ?.map((item, index, sorted) => (
                      <LinkedPatientRow
                        key={`${item.id}-${index}`}
                        item={item}
                        contact={contact}
                        providerNames={providerNames}
                        isLast={index === sorted.length - 1}
                      />
                    ))
                ) : (
                  <></>
                )}
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>
      ) : (
        <div className="flex items-center gap-x-1 overflow-hidden rounded border-[0.5px] border-default bg-orange-500/8 py-2 pl-4 pr-2 text-label-xs text-orange-800">
          <WarningCircle width={16} height={16} />
          Missing PMS integration
        </div>
      )}
    </div>
  )
}
